/**
 * Billiards Canvas Component
 *
 * Renders the billiards table using HTML5 Canvas.
 * Handles: table rendering, ball drawing, cue stick aiming,
 * trajectory guide, pocketed balls display.
 * Responsive - scales to fit container while maintaining 2:1 aspect ratio.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  BilliardsBall,
  BilliardsGameState,
  AimState,
  Shot,
  SpinType,
  Vector2D,
  TABLE_WIDTH,
  TABLE_HEIGHT,
  CUSHION_WIDTH,
  BALL_RADIUS,
  POCKET_POSITIONS,
  POCKET_RADIUS,
  BALL_COLORS,
} from '../types';
import { BilliardsPhysics } from '../BilliardsPhysics';
import './BilliardsCanvas.css';

interface BilliardsCanvasProps {
  gameState: BilliardsGameState;
  physics: BilliardsPhysics;
  isSimulating: boolean;
  isAiming: boolean;
  ballInHand: boolean;
  onShot: (shot: Shot) => void;
  onPlaceCueBall: (x: number, y: number) => void;
  disabled: boolean;
}

// Drawing helper functions (pure, no state)
function drawBall(ctx: CanvasRenderingContext2D, ball: BilliardsBall): void {
  const color = BALL_COLORS[ball.number] || '#FFFFFF';

  ctx.save();

  // Ball shadow
  ctx.beginPath();
  ctx.arc(ball.x + 1.5, ball.y + 1.5, ball.radius, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.fill();

  if (ball.type === 'stripe') {
    // Stripe pattern: white base with colored stripe band
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();

    // Draw stripe band (clip to ball circle)
    ctx.save();
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = color;
    ctx.fillRect(ball.x - ball.radius, ball.y - ball.radius * 0.5, ball.radius * 2, ball.radius);
    ctx.restore();
  } else {
    // Solid ball
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }

  // Ball highlight (glossy effect)
  const gradient = ctx.createRadialGradient(
    ball.x - ball.radius * 0.3,
    ball.y - ball.radius * 0.3,
    0,
    ball.x,
    ball.y,
    ball.radius
  );
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.beginPath();
  ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
  ctx.fillStyle = gradient;
  ctx.fill();

  // Ball number (skip for cue ball)
  if (ball.number > 0) {
    // White circle for number
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.radius * 0.45, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();

    // Number text
    ctx.fillStyle = '#000000';
    ctx.font = `bold ${ball.radius * 0.7}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(ball.number.toString(), ball.x, ball.y + 0.5);
  }

  // Ball outline
  ctx.beginPath();
  ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.lineWidth = 0.5;
  ctx.stroke();

  ctx.restore();
}

function drawAimGuide(ctx: CanvasRenderingContext2D, cueBall: BilliardsBall, aim: AimState): void {
  const guideLength = 150;
  const endX = cueBall.x + Math.cos(aim.angle) * guideLength;
  const endY = cueBall.y + Math.sin(aim.angle) * guideLength;

  ctx.beginPath();
  ctx.setLineDash([4, 4]);
  ctx.moveTo(cueBall.x, cueBall.y);
  ctx.lineTo(endX, endY);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawCueStick(ctx: CanvasRenderingContext2D, cueBall: BilliardsBall, aim: AimState): void {
  const stickLength = 180;
  const pullBack = 10 + aim.power * 60; // Pull back based on power
  const startDist = BALL_RADIUS + 5 + pullBack;
  const oppositeAngle = aim.angle + Math.PI;

  const startX = cueBall.x + Math.cos(oppositeAngle) * startDist;
  const startY = cueBall.y + Math.sin(oppositeAngle) * startDist;
  const endX = startX + Math.cos(oppositeAngle) * stickLength;
  const endY = startY + Math.sin(oppositeAngle) * stickLength;

  // Cue stick body
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.strokeStyle = '#D4A84B';
  ctx.lineWidth = 4;
  ctx.stroke();

  // Cue tip
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  const tipEndX = startX + Math.cos(aim.angle) * 3;
  const tipEndY = startY + Math.sin(aim.angle) * 3;
  ctx.lineTo(tipEndX, tipEndY);
  ctx.strokeStyle = '#87CEEB';
  ctx.lineWidth = 5;
  ctx.stroke();

  // Power indicator bar
  const barX = TABLE_WIDTH - 30;
  const barY = CUSHION_WIDTH + 10;
  const barHeight = TABLE_HEIGHT - CUSHION_WIDTH * 2 - 20;
  const filledHeight = barHeight * aim.power;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.fillRect(barX, barY, 15, barHeight);

  // Power gradient (green to red)
  const powerGradient = ctx.createLinearGradient(barX, barY + barHeight, barX, barY);
  powerGradient.addColorStop(0, '#00ff00');
  powerGradient.addColorStop(0.5, '#ffff00');
  powerGradient.addColorStop(1, '#ff0000');
  ctx.fillStyle = powerGradient;
  ctx.fillRect(barX, barY + barHeight - filledHeight, 15, filledHeight);

  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1;
  ctx.strokeRect(barX, barY, 15, barHeight);
}

export const BilliardsCanvas: React.FC<BilliardsCanvasProps> = ({
  gameState,
  physics,
  isSimulating,
  isAiming,
  ballInHand,
  onShot,
  onPlaceCueBall,
  disabled,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  const [scale, setScale] = useState(1);
  const [aimState, setAimState] = useState<AimState>({
    isAiming: false,
    angle: 0,
    power: 0,
    spin: 'none',
  });
  const [dragStart, setDragStart] = useState<Vector2D | null>(null);
  const [selectedSpin, setSelectedSpin] = useState<SpinType>('none');

  // Responsive scaling
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const scaleX = containerWidth / TABLE_WIDTH;
        setScale(Math.min(scaleX, 1.5));
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Render function
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = TABLE_WIDTH * scale;
    const h = TABLE_HEIGHT * scale;
    canvas.width = w;
    canvas.height = h;

    ctx.save();
    ctx.scale(scale, scale);

    // Draw table background (wood frame)
    ctx.fillStyle = '#5C3317';
    ctx.fillRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);

    // Draw felt (green playing surface)
    ctx.fillStyle = '#0B6623';
    ctx.fillRect(CUSHION_WIDTH, CUSHION_WIDTH, TABLE_WIDTH - CUSHION_WIDTH * 2, TABLE_HEIGHT - CUSHION_WIDTH * 2);

    // Draw cushion detail lines
    ctx.strokeStyle = '#084D1A';
    ctx.lineWidth = 1;
    ctx.strokeRect(CUSHION_WIDTH + 2, CUSHION_WIDTH + 2, TABLE_WIDTH - CUSHION_WIDTH * 2 - 4, TABLE_HEIGHT - CUSHION_WIDTH * 2 - 4);

    // Draw pockets
    for (const pocket of POCKET_POSITIONS) {
      ctx.beginPath();
      ctx.arc(pocket.x, pocket.y, POCKET_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = '#1a1a1a';
      ctx.fill();
      // Pocket rim
      ctx.beginPath();
      ctx.arc(pocket.x, pocket.y, POCKET_RADIUS + 2, 0, Math.PI * 2);
      ctx.strokeStyle = '#3d2817';
      ctx.lineWidth = 3;
      ctx.stroke();
    }

    // Draw head string line (faint)
    ctx.beginPath();
    ctx.setLineDash([5, 5]);
    ctx.moveTo(TABLE_WIDTH * 0.25, CUSHION_WIDTH);
    ctx.lineTo(TABLE_WIDTH * 0.25, TABLE_HEIGHT - CUSHION_WIDTH);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw foot spot
    ctx.beginPath();
    ctx.arc(TABLE_WIDTH * 0.73, TABLE_HEIGHT / 2, 3, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.fill();

    // Draw balls
    const activeBalls = physics.getActiveBalls();
    for (const ball of activeBalls) {
      drawBall(ctx, ball);
    }

    // Draw aim line and cue stick
    if (aimState.isAiming && !isSimulating && !ballInHand) {
      const cueBall = activeBalls.find((b) => b.type === 'cue');
      if (cueBall) {
        drawAimGuide(ctx, cueBall, aimState);
        drawCueStick(ctx, cueBall, aimState);
      }
    }

    // Draw ball-in-hand indicator
    if (ballInHand && !isSimulating) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Click to place cue ball', TABLE_WIDTH / 2, TABLE_HEIGHT - 8);
    }

    ctx.restore();
  }, [scale, aimState, isSimulating, ballInHand, physics]);

  // Animation loop
  useEffect(() => {
    const animate = (timestamp: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const dt = Math.min((timestamp - lastTimeRef.current) / 1000, 1 / 30);
      lastTimeRef.current = timestamp;

      if (isSimulating) {
        physics.step(dt);
      }

      renderCanvas();
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isSimulating, physics, renderCanvas]);

  // Mouse/touch event handlers
  const getCanvasCoords = (e: React.MouseEvent | React.TouchEvent): Vector2D => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    return {
      x: (clientX - rect.left) / scale,
      y: (clientY - rect.top) / scale,
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled || isSimulating) return;
    const coords = getCanvasCoords(e);

    if (ballInHand) {
      onPlaceCueBall(coords.x, coords.y);
      return;
    }

    const cueBall = physics.getActiveBalls().find((b) => b.type === 'cue');
    if (!cueBall) return;

    // Start aiming - angle points from cue ball toward click
    const angle = Math.atan2(coords.y - cueBall.y, coords.x - cueBall.x);
    setAimState({ isAiming: true, angle, power: 0.3, spin: selectedSpin });
    setDragStart(coords);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (disabled || isSimulating || !aimState.isAiming || !dragStart) return;
    const coords = getCanvasCoords(e);

    const cueBall = physics.getActiveBalls().find((b) => b.type === 'cue');
    if (!cueBall) return;

    // Angle from cue ball to current mouse position
    const angle = Math.atan2(coords.y - cueBall.y, coords.x - cueBall.x);

    // Power based on distance from cue ball
    const dist = Math.sqrt(
      Math.pow(coords.x - cueBall.x, 2) + Math.pow(coords.y - cueBall.y, 2)
    );
    const power = Math.min(1, Math.max(0.1, dist / 300));

    setAimState((prev) => ({ ...prev, angle, power, spin: selectedSpin }));
  };

  const handleMouseUp = () => {
    if (disabled || isSimulating || !aimState.isAiming) return;

    if (aimState.power > 0.05) {
      onShot({
        angle: aimState.angle,
        power: aimState.power,
        spin: aimState.spin,
      });
    }

    setAimState({ isAiming: false, angle: 0, power: 0, spin: 'none' });
    setDragStart(null);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (disabled || isSimulating) return;
    const coords = getCanvasCoords(e);

    if (ballInHand) {
      onPlaceCueBall(coords.x, coords.y);
      return;
    }

    const cueBall = physics.getActiveBalls().find((b) => b.type === 'cue');
    if (!cueBall) return;

    const angle = Math.atan2(coords.y - cueBall.y, coords.x - cueBall.x);
    setAimState({ isAiming: true, angle, power: 0.3, spin: selectedSpin });
    setDragStart(coords);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (disabled || isSimulating || !aimState.isAiming) return;
    const coords = getCanvasCoords(e);

    const cueBall = physics.getActiveBalls().find((b) => b.type === 'cue');
    if (!cueBall) return;

    const angle = Math.atan2(coords.y - cueBall.y, coords.x - cueBall.x);
    const dist = Math.sqrt(
      Math.pow(coords.x - cueBall.x, 2) + Math.pow(coords.y - cueBall.y, 2)
    );
    const power = Math.min(1, Math.max(0.1, dist / 300));

    setAimState((prev) => ({ ...prev, angle, power, spin: selectedSpin }));
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    if (disabled || isSimulating || !aimState.isAiming) return;

    if (aimState.power > 0.05) {
      onShot({
        angle: aimState.angle,
        power: aimState.power,
        spin: aimState.spin,
      });
    }

    setAimState({ isAiming: false, angle: 0, power: 0, spin: 'none' });
    setDragStart(null);
  };

  return (
    <div className="billiards-canvas-container" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className="billiards-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          width: `${TABLE_WIDTH * scale}px`,
          height: `${TABLE_HEIGHT * scale}px`,
        }}
      />
      <div className="billiards-spin-selector">
        <span className="billiards-spin-label">Spin:</span>
        {(['none', 'top', 'back', 'left', 'right'] as SpinType[]).map((spin) => (
          <button
            key={spin}
            className={`billiards-spin-btn ${selectedSpin === spin ? 'billiards-spin-btn--active' : ''}`}
            onClick={() => setSelectedSpin(spin)}
            disabled={disabled || isSimulating}
          >
            {spin === 'none' ? 'None' : spin.charAt(0).toUpperCase() + spin.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
};
